import { useMutation, useQuery } from '@tanstack/react-query';
import { Form } from 'antd';
import { useState } from 'react';
import { IntlShape, useIntl } from 'react-intl';
import { scheduleSettingApi } from '../../../apis';
import { CreateScheduleSettingDto, ScheduleSetting } from '../../../apis/client-axios';
import ButtonWrap from '../../../components/ButtonWrap';
import InputWrap from '../../../components/InputWrap';
import ModalWrap from '../../../components/ModalWrap';
import TableWrap from '../../../components/TableWrap';
import ToastError from '../../../components/Toast/ToastError';
import ToastSuccess from '../../../components/Toast/ToastSuccess';
import Widget from '../../../components/Widget';
import SvgEdit from '../../../components/svg/SvgEdit/index';
import { SCHEDULE_MANAGEMENT } from '../../../constants/QueryKey';
import CustomColorPicker from './components/CustomColorPicker';
import RgbaColorPickerCustom from './components/RgbaColorPickerCustom';
import './style.scss';

const ScheduleManagement = () => {
  const intl = useIntl();
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [selectedRowData, setSelectedRowData] = useState<ScheduleSetting | null>(null);
  const [colorPicker, setcolorPicker] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [form] = Form.useForm();
  const {
    data: scheduleData,
    refetch,
    isLoading,
  } = useQuery({
    queryKey: [SCHEDULE_MANAGEMENT],
    queryFn: () => scheduleSettingApi.scheduleSettingControllerGetList(),
  });
  const editScheduleMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: CreateScheduleSettingDto }) => {
      return scheduleSettingApi.scheduleSettingControllerUpdate(id, payload);
    },
    onSuccess: () => {
      setOpenModal(false);
      refetch();
      ToastSuccess({
        content: intl.formatMessage({ id: 'schedule.editSuccess' }),
      });
      setLoading(false);
    },
    onError: (error) => {
      if (error.message === 'Request failed with status code 400') {
        ToastError({
          content: intl.formatMessage({ id: 'schedule.beenCreated' }),
        });
        setOpenModal(false);
        setLoading(false);
      }
    },
  });
  const handleEditSchedule = () => {
    form
      .validateFields()
      .then((values) => {
        const payload: CreateScheduleSettingDto = {
          text: form.getFieldValue('editSchedule'),
          color: colorPicker,
        };
        editScheduleMutation.mutate({ id: selectedRowData?.id as string, payload });
        setLoading(true);
      })
      .catch((error) => {
        console.error('Validation failed:', error);
      });
  };

  const columns = (intl: IntlShape) => [
    {
      title: intl.formatMessage({ id: 'schedule.distinction' }),
      dataIndex: 'type',
      key: 'type',
      render: (record) => {
        return (
          <>
            <p className="schedule_management-content-data">{intl.formatMessage({ id: `schedule.${record}` })}</p>
          </>
        );
      },
    },
    {
      title: intl.formatMessage({ id: 'schedule.display' }),
      dataIndex: 'text',
      key: 'text',
      render: (record) => {
        return (
          <div className="schedule_management-content-name">
            <p className="schedule_management-content-name-tool">{record}</p>
          </div>
        );
      },
    },
    {
      title: intl.formatMessage({ id: 'schedule.color' }),
      dataIndex: 'color',
      key: 'color',
      render: (record) => {
        return (
          <>
            <CustomColorPicker
              colorPickerProps={{
                // showText: () => intl.formatMessage({ id: 'schedule.select' }),
                value: record,
                className: 'schedule_management-content-pickerColor',
                onChange: () => setcolorPicker(record),
                disabled: true,
              }}
            />
          </>
        );
      },
    },
    {
      title: '',
      dataIndex: 'option',
      key: 'option',
      render: (record, row) => {
        return (
          <>
            <ButtonWrap
              className="schedule_management-content-button-edit"
              onClick={() => {
                setcolorPicker(row.color);
                setSelectedRowData(row);
                setOpenModal(true);
                form.setFieldValue('editSchedule', row.text);
              }}
              loading={isLoading}
            >
              <SvgEdit />
              {intl.formatMessage({ id: 'schedule.edit' })}
            </ButtonWrap>
          </>
        );
      },
    },
  ];
  return (
    <div className="schedule_management">
      <div className="schedule_management-content">
        <Widget
          breadcrumbs={[intl.formatMessage({ id: 'schedule.settting' })]}
          content={
            <TableWrap
              isPagination={false}
              loading={isLoading}
              tableProps={{
                dataSource: scheduleData?.data?.data || [],
                columns: columns(intl),
                className: 'schedule_management-content-table',
              }}
            />
          }
          title={intl.formatMessage({ id: 'schedule.settting' })}
        />
      </div>
      <div className="schedule_management-modal">
        {openModal && (
          <ModalWrap
            open={openModal}
            cancelText={intl.formatMessage({ id: 'schedule.cancel' })}
            submitText={intl.formatMessage({ id: 'schedule.keep' })}
            onSubmit={handleEditSchedule}
            onCancel={() => {
              setOpenModal(false);
              form.resetFields();
            }}
            type="upsert"
            title={intl.formatMessage({ id: 'schedule.modalTitle' })}
            buttonSubmitProps={{
              loading: loading,
            }}
            body={
              <Form form={form} layout="vertical">
                <Form.Item
                  name="editSchedule"
                  rules={[
                    {
                      required: true,
                      message: intl.formatMessage({ id: 'schedule.required' }),
                    },
                    {
                      whitespace: true,
                      message: intl.formatMessage({ id: 'schedule.noWhiteSpace' }),
                    },
                  ]}
                  label={intl.formatMessage({ id: 'schedule.shortenedDisplay' })}
                >
                  <InputWrap
                    placeholder={intl.formatMessage({ id: `schedule.holydayPaid` })}
                    className={`schedule_management-modal-input`}
                    required={true}
                    maxLength={255}
                  />
                </Form.Item>
                <div className="schedule_management-modal-shortened">
                  <RgbaColorPickerCustom
                    onHexColorChange={(hexColor) => {
                      setcolorPicker(hexColor);
                    }}
                    defaultValue={colorPicker}
                  />
                </div>
              </Form>
            }
          />
        )}
      </div>
    </div>
  );
};
export default ScheduleManagement;
